/**
 * A Combobox which is linked to a text input.
 *
 * Following WAI-ARIA guidelines, the component combobox is intended to include:
 *  - A single-select listbox popup
 *  - With list autocomplete with manual selection
 *
 * https://www.w3.org/TR/wai-aria-practices-1.2/#combobox
 * https://www.w3.org/TR/wai-aria-practices-1.2/#Listbox
 */

import * as React from "react";
import { createPortal } from "react-dom";
import classNames from "classnames";
import { observer } from "mobx-react";
import { Transition } from "react-transition-group";
import { SearchInput } from "../../..";
import { TextInputSize, TextInputTheme, TextInputVariant } from "../TextInput/TextInput";
import { action, computed, observable } from "mobx";

import { randomString, KeyType, MouseButtonType } from "../../../utils";
import { AnimationDuration } from "../../../styles/Animations";
import Panel, { PanelTheme } from "../panel/Panel/Panel";
import getPortalRoot from "../../../utils/getPortalRoot";
import observeRect from "@reach/observe-rect";
import getOverlayPosition, { OverlayPositionProps } from "../../../utils/getOverlayPosition";
import { TransitionStatus } from "react-transition-group/Transition";
import forceReflow from "../../../utils/forceReflow";
import ReactResizeDetector from "react-resize-detector/build/withPolyfill";

export interface ComboboxEntry<T extends string> {
  label: string;
  value: T;
}

export type ComboboxInputProps = {
  value: string;
  onChange: (value: string) => void;
  onFocus: (event: React.FocusEvent<HTMLElement>) => void;
  onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
  ref: React.RefObject<HTMLInputElement>;
} & Pick<
  React.HTMLProps<HTMLInputElement>,
  | "placeholder"
  | "role"
  | "aria-expanded"
  | "aria-required"
  | "aria-activedescendant"
  | "aria-controls"
  | "aria-autocomplete"
>;

export interface IComboboxProps<T extends string> {
  entries: ComboboxEntry<T>[];
  startingSearchTerm?: string;
  autoFocus: boolean;
  noEntriesMessage: React.ReactNode;
  noResultsMessage: React.ReactNode;
  onSelect: (value: ComboboxEntry<T>) => void;
  clearOnSelect: boolean;
  onCancel?: () => void;
  onChange?: (currSearchTerm: string) => void;
  label: string;
  renderEntry?: (entry: ComboboxEntry<T>) => JSX.Element;
  theme?: TextInputTheme;
  onFilter?: (entry: ComboboxEntry<T>, searchTerm: string) => boolean;
  placeholder?: string;
  variant: TextInputVariant;
  size: TextInputSize.Small | TextInputSize.Medium;
  startElement?: React.ReactElement | null;
  endElement?: React.ReactElement | null;
  className?: string;
  /*
   * A render prop for the Combobox input
   * Defaults to the SearchInput component
   */
  renderInput?: (props: ComboboxInputProps) => JSX.Element;
}

@observer
export default class Combobox<T extends string> extends React.Component<IComboboxProps<T>> {
  public static defaultProps = {
    autoFocus: false,
    clearOnSelect: false,
    noEntriesMessage: "No items available",
    noResultsMessage: "No results found",
    variant: TextInputVariant.Bordered,
    size: TextInputSize.Medium,
    theme: TextInputTheme.Light,
  };

  /**
   * There are times that we want the input box to be reflecting what the user is filtering the entries by, however we
   * sometimes want to just display whatever is currently selected (e.g. when a user is navigating down the list with
   * key presses), so we need two values to juggle what we display versus what we filter by
   */
  @observable.ref
  private searchTerm: string;
  @observable.ref
  private displayOnlyTerm: string | undefined;

  @observable.ref
  private selectedValue: T | null = null;

  private selectedElemRef = React.createRef<HTMLDivElement>();

  @observable.ref
  private isFocused = false;

  @observable.ref
  private inputWidth = 0;

  private textInputRef = React.createRef<HTMLInputElement>();

  private comboListDomId: string;

  public componentDidMount(): void {
    this.setInputWidth();

    if (this.props.autoFocus) {
      setTimeout(() => {
        if (this.textInputRef.current != null) {
          this.textInputRef.current.select();
        }
      });
    }
  }

  constructor(props: IComboboxProps<T>) {
    super(props);

    this.searchTerm = props.startingSearchTerm ?? "";
    this.comboListDomId = `combobox${randomString().toLowerCase()}`;
  }

  public render(): JSX.Element {
    /*
     * When using the renderInput prop we don't want to add a wrapping element
     * As this is left up to the caller to control
     */
    return (
      <ComboboxWrapper
        className={classNames("combobox", `combobox--${this.props.theme}`)}
        useWrapper={!this.props.renderInput}
      >
        <ReactResizeDetector handleWidth onResize={this.setInputWidth}>
          {this.renderComboboxInput()}
        </ReactResizeDetector>
        <Transition
          mountOnEnter
          unmountOnExit
          in={this.isFocused}
          timeout={AnimationDuration.Long}
          onEnter={forceReflow}
        >
          {(status) => (
            <ComboboxListBox
              noEntriesMessage={this.props.noEntriesMessage}
              noResultsMessage={this.props.noResultsMessage}
              entries={this.props.entries}
              handleSelect={this.handleSelect}
              getIdForEntry={this.getIdForEntry}
              textInputRef={this.textInputRef}
              selectedElemRef={this.selectedElemRef}
              filteredList={this.filteredList}
              selectedValue={this.selectedValue}
              theme={this.props.theme}
              renderEntry={this.props.renderEntry}
              isFocused={this.isFocused}
              comboListDomId={this.comboListDomId}
              className={this.props.className}
              transitionStatus={status}
              listWidth={this.inputWidth}
            />
          )}
        </Transition>
      </ComboboxWrapper>
    );
  }

  private renderComboboxInput(): JSX.Element {
    const commonInputProps: ComboboxInputProps = {
      placeholder: this.props.placeholder,
      value: this.displayOnlyTerm || this.searchTerm,
      ref: this.textInputRef,
      role: "combobox",
      "aria-expanded": this.isFocused ? "true" : "false",
      "aria-required": "true",
      "aria-activedescendant":
        this.isFocused && this.selectedValue ? this.getIdForEntry(this.selectedValue) : undefined,
      "aria-controls": this.isFocused ? this.comboListDomId : undefined,
      "aria-autocomplete": "list",
      onChange: (value: string) => {
        this.searchTerm = value;
        this.displayOnlyTerm = "";
        this.selectedValue = null;
        if (this.props.onChange) {
          this.props.onChange(this.searchTerm);
        }
      },
      onKeyDown: this.onKeyDown,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
    };

    if (!!this.props.renderInput) {
      return this.props.renderInput(commonInputProps);
    } else {
      return (
        <SearchInput
          {...commonInputProps}
          label={this.props.label}
          theme={this.props.theme || TextInputTheme.Dark}
          variant={this.props.variant}
          size={this.props.size}
          startElement={this.props.startElement}
          endElement={this.props.endElement}
        />
      );
    }
  }

  private getIdForEntry = (entryValue: string): string => {
    return `comboboxentry${entryValue.toLowerCase().replace(/[^a-z0-9]/, "")}`;
  };

  @action
  private setInputWidth = () => {
    this.inputWidth = this.textInputRef.current?.getBoundingClientRect().width || 0;
  };

  private handleSelect = (entry: ComboboxEntry<T>) => {
    this.props.onSelect(entry);

    if (this.props.clearOnSelect) {
      this.searchTerm = "";
      this.displayOnlyTerm = "";
      this.selectedValue = null;
    }
  };

  private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();

    switch (event.key) {
      case KeyType.ArrowDown:
        const nextItem = this.getNextItem();
        if (nextItem) {
          this.selectedValue = nextItem.value;
          this.displayOnlyTerm = nextItem.label;
          setTimeout(() => {
            this.selectedElemRef.current?.scrollIntoView({
              block: "center",
            });
          });
        }
        event.preventDefault();
        break;
      case KeyType.ArrowUp:
        const previousItem = this.getPreviousItem();
        if (previousItem) {
          this.selectedValue = previousItem.value;
          this.displayOnlyTerm = previousItem.label;
          setTimeout(() => {
            this.selectedElemRef.current?.scrollIntoView({
              block: "center",
            });
          });
        }
        event.preventDefault();
        break;
      case KeyType.Enter:
        if (this.selectedValue) {
          const index = this.getFullListIndex(this.selectedValue);
          this.handleSelect(this.props.entries[index]);
        } else {
          const index = this.getFullListIndex(this.displayOnlyTerm || this.searchTerm);
          if (index >= 0) {
            this.handleSelect(this.props.entries[index]);
          }
        }
        this.textInputRef.current?.blur();
        event.preventDefault();
        break;
      case KeyType.Escape:
        event.preventDefault();
        this.textInputRef.current?.blur();
        this.props.onCancel?.();
        break;
    }
  };

  private getNextItem = (): ComboboxEntry<T> | null => {
    const index = this.getFilteredIndex(this.selectedValue);

    const entries = this.filteredList;
    if (index + 1 >= entries.length) {
      return entries[0];
    } else {
      return entries[index + 1];
    }
  };

  private getPreviousItem = (): ComboboxEntry<T> | null => {
    const index = this.getFilteredIndex(this.selectedValue);

    if (index - 1 < 0) {
      return this.filteredList[this.filteredList.length - 1];
    } else {
      return this.filteredList[index - 1];
    }
  };

  private getFilteredIndex = (searchValue: T | null): number => {
    const entries = this.filteredList;
    const index = entries.findIndex((entry) => {
      return entry.value === searchValue;
    });

    return index;
  };

  private getFullListIndex = (searchValue: string): number => {
    const index = this.props.entries.findIndex((entry) => {
      return entry.value === searchValue;
    });

    return index;
  };

  @computed
  private get filteredList(): ComboboxEntry<T>[] {
    const { onFilter, entries } = this.props;
    if (!!onFilter) {
      return entries.filter((entry) => onFilter(entry, this.searchTerm));
    }

    if (this.searchTerm === "") {
      return this.props.entries;
    }

    return this.props.entries.filter((entry) => {
      return entry.label.toLowerCase().indexOf(this.searchTerm.toLowerCase()) >= 0;
    });
  }

  @action
  private onFocus = () => {
    this.isFocused = true;
  };

  @action
  private onBlur = () => {
    this.isFocused = false;
  };
}

const ComboboxWrapper: React.FC<{
  className: string;
  useWrapper: boolean;
}> = (props) => {
  const { className, useWrapper, children } = props;

  if (useWrapper) {
    return <div className={className}>{children}</div>;
  } else {
    return <>{children}</>;
  }
};

interface ComboboxListBoxProps<T extends string> {
  noEntriesMessage: React.ReactNode;
  noResultsMessage: React.ReactNode;
  entries: ComboboxEntry<T>[];
  handleSelect: (entry: ComboboxEntry<T>) => void;
  getIdForEntry: (entryValue: string) => string;
  textInputRef: React.RefObject<HTMLElement>;
  selectedElemRef: React.RefObject<HTMLDivElement>;
  filteredList: ComboboxEntry<T>[];
  selectedValue: T | null;
  theme?: TextInputTheme;
  renderEntry?: (entry: ComboboxEntry<T>) => JSX.Element;
  isFocused: boolean;
  comboListDomId: string;
  className?: string;
  transitionStatus: TransitionStatus;
  listWidth: number;
}

@observer
class ComboboxListBox<T extends string> extends React.Component<ComboboxListBoxProps<T>> {
  private portalRoot = getPortalRoot("combobox-root");
  private rectObserver?: { observe: () => void; unobserve: () => void };
  private listRef: React.RefObject<HTMLDivElement> = React.createRef();

  @observable.ref
  private listPosition?: OverlayPositionProps;

  public componentDidMount(): void {
    this.rectObserver = observeRect(this.props.textInputRef.current as HTMLElement, this.handleRectObserver);
    this.rectObserver?.observe();
  }

  public componentDidUpdate(prevProps: ComboboxListBoxProps<T>): void {
    if (this.props.textInputRef.current && prevProps.listWidth !== this.props.listWidth) {
      this.handleRectObserver(this.props.textInputRef.current.getBoundingClientRect());
    }
  }

  public componentWillUnmount(): void {
    this.rectObserver?.unobserve();
  }

  @action
  private handleRectObserver = (targetRect: DOMRect): void => {
    const listRect = this.listRef.current?.getBoundingClientRect();

    this.listPosition = getOverlayPosition({
      targetRect,
      overlayRect: listRect,
      position: "bottom",
      offset: 8,
      outerMargin: 8,
    });
  };

  private renderList() {
    if (this.props.entries.length < 1) {
      return <div className="combobox__empty-message">{this.props.noEntriesMessage}</div>;
    }

    if (this.props.filteredList.length < 1) {
      return <div className="combobox__empty-message">{this.props.noResultsMessage}</div>;
    }

    return this.props.filteredList.map((entry) => {
      const isSelected = entry.value === this.props.selectedValue;
      return (
        <div
          id={this.props.getIdForEntry(entry.value)}
          key={entry.value}
          className={classNames("combobox__entry", `combobox-entry--${this.props.theme}`, {
            "combobox__entry--selected": isSelected,
          })}
          aria-selected={isSelected ? "true" : "false"}
          role="option"
          ref={isSelected ? this.props.selectedElemRef : null}
          onMouseDown={(e) => {
            e.stopPropagation();
            e.nativeEvent.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();

            if (e.button === MouseButtonType.Left) {
              this.props.handleSelect(entry);
            }
          }}
        >
          {this.renderEntry(entry)}
        </div>
      );
    });
  }

  private renderEntry = (entry: ComboboxEntry<T>): JSX.Element => {
    if (this.props.renderEntry != null) {
      return this.props.renderEntry(entry);
    } else {
      return (
        <span
          className={classNames("combobox__default-entry-label", `combobox__default-entry-label--${this.props.theme}`)}
        >
          {entry.label}
        </span>
      );
    }
  };

  render() {
    return createPortal(
      <div
        ref={this.listRef}
        className={classNames("combobox__combo-holder", this.props.className)}
        style={{
          transform: `translate3d(${this.listPosition?.overlay.left || 0}px, ${
            this.listPosition?.overlay.top || 0
          }px, 0)`,
          width: this.props.listWidth,
        }}
      >
        <Panel
          className={classNames("combobox__combo", `combobox__combo--${this.props.transitionStatus}`)}
          id={this.props.comboListDomId}
          role="listbox"
          theme={this.props.theme === TextInputTheme.Dark ? PanelTheme.Dark : PanelTheme.Light}
        >
          <div className={classNames("combobox__combo-content", `combobox__combo-content--${this.props.theme}`)}>
            {this.renderList()}
          </div>
        </Panel>
      </div>,
      this.portalRoot as Element,
    );
  }
}
